import { useQueryGetUserTaxProfile } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type Nullable } from '@flecto/types'
import { type TaxInfo } from '@flecto/types'
import { valueFromCents } from '@flecto/ui/lib/utils'
import { Price } from '@flecto/utils/src/Number'
import NumberHelper from '@flecto/utils/src/Number/Calculation'

export type TaxBracket = {
  label: string
  value: string
  percentage: number
}

export type CalculatedAmounts = {
  input: Price
  price: Price
  taxes: Price
  display: Price
  totalWithTaxes: Price
  totalWithoutTaxes: Price
  description: string
}

/**
 * This hook connects into user's tax profile and return information related
 * to taxes, like available brackets, settings and helper functions to calculate
 * prices properly according with available brackets list.
 */
export function useTaxesInfo() {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_taxes' })
  const { data, isLoading, isSuccess } = useQueryGetUserTaxProfile()
  const userSettingShowPricesWithTaxes = Boolean(
    data?.show_prices_with_tax ?? true
  )
  const brackets: TaxBracket[] = []
  const userDefaultTaxBracket: TaxBracket = {
    value: '',
    label: '',
    percentage: 0,
  }
  const userDefaultTaxable = data?.default_taxable ?? false

  /**
   * This will return a tax bracket based on key 'standard', 'intermediate', etc.,
   * but key also can be a plain percentage string number when country has no
   * invoicing supported. In this case the returned tax bracket will be the own
   * percentage.
   *
   * @returns TaxBracket
   */
  const getTaxBracket = (key: Nullable<string>): TaxBracket => {
    const sameAsKeyBracket = {
      label: `${key}%`,
      percentage: Number.isNaN(Number(key)) ? 0 : Number(key) / 10,
      value: String(key),
    }

    if (!data?.meta.invoicing_supported) {
      return sameAsKeyBracket
    }

    return brackets.find((bracket) => bracket.value === key) || sameAsKeyBracket
  }

  /**
   * This will return the correct values for a price, taking into consideration
   * current user's configs to show prices with or without taxes. The returned
   * object keys are:
   *
   * - display:           price to be displayed to user
   * - input:             price to be put into input as default/initial value (when editing)
   * - price:             price to be sent to API
   * - taxes:             taxes amount to be displayed to user
   * - totalWithTaxes:    price to be displayed to user for total + taxes
   * - totalWithoutTaxes: price to be displayed to user for total - taxes
   */
  const getCalculatedAmounts = (
    priceCents: number,
    taxRate: string,
    isTaxable: boolean,
    isInputCents = false,
    forcePricesWithTaxes = false
  ): CalculatedAmounts => {
    const taxBracket = getTaxBracket(taxRate)
    const percentage = isTaxable ? (taxBracket.percentage ?? 0) : 0
    const amounts: CalculatedAmounts = {
      taxes: new Price(0),
      input: new Price(0),
      price: new Price(0),
      display: new Price(0),
      totalWithTaxes: new Price(0),
      totalWithoutTaxes: new Price(0),
      description: '',
    }

    const price = Math.ceil(
      isInputCents && (userSettingShowPricesWithTaxes || forcePricesWithTaxes)
        ? new NumberHelper(new Price(priceCents).cents).divide(
            1 + percentage / 100
          ).value
        : priceCents
    )

    if (userSettingShowPricesWithTaxes || forcePricesWithTaxes) {
      amounts.price = new Price(price)

      amounts.totalWithoutTaxes = new Price(price)
      amounts.totalWithTaxes = valueFromCents(
        Math.ceil(new NumberHelper(price).addPercent(percentage).value)
      )
      amounts.taxes = new Price(amounts.totalWithTaxes.cents - price)

      amounts.description = `${t('including_taxes')} (${percentage}%)`
      amounts.display = valueFromCents(amounts.totalWithTaxes.cents)
      amounts.input = valueFromCents(amounts.totalWithTaxes.cents)
    } else {
      amounts.input = new Price(price)
      amounts.price = new Price(price)
      amounts.display = new Price(price)
      amounts.description = `${t('excluding_taxes')} (${percentage}%)`

      amounts.taxes = valueFromCents(
        Math.ceil(new NumberHelper(price).addPercent(percentage).value - price)
      )

      amounts.totalWithoutTaxes = new Price(price)
      amounts.totalWithTaxes = valueFromCents(
        Math.ceil(new NumberHelper(price).addPercent(percentage).value)
      )
    }

    return amounts
  }

  if (!data?.meta?.invoicing_supported || isLoading) {
    return {
      isLoading,
      isSuccess,
      isTaxBracketsSupported: false,
      brackets: [],
      userDefaultTaxable,
      userDefaultTaxBracket: getTaxBracket(data?.default_tax_rate ?? '0'),
      userSettingShowPricesWithTaxes: data?.show_prices_with_tax ?? true,
      getTaxBracket,
      getCalculatedAmounts,
    }
  }

  const taxCategory = data.meta?.tax_category || 'VAT'

  Object.keys(data.meta?.tax_info).forEach((taxLevelKey) => {
    const taxInfo = data.meta?.tax_info as Record<string, TaxInfo | undefined>

    const chargeInfo = taxInfo[taxLevelKey]

    if (chargeInfo) {
      const charge = chargeInfo.charge / 10
      const surcharge = chargeInfo.surcharge ? chargeInfo.surcharge / 10 : 0

      const label = surcharge
        ? `${t(`taxes_labels.${taxLevelKey}`)} (${taxCategory.toUpperCase()} ${charge}% + ${surcharge}%)`
        : `${t(`taxes_labels.${taxLevelKey}`)} (${taxCategory.toUpperCase()} ${charge}%)`

      const isChecked = data.default_tax_rate === taxLevelKey

      if (isChecked) {
        userDefaultTaxBracket.label = label
        userDefaultTaxBracket.value = taxLevelKey
        userDefaultTaxBracket.percentage = charge
      }

      // No show itens with surcharge even definition from product by jLoff
      if (!surcharge) {
        brackets.push({
          label,
          value: taxLevelKey,
          percentage: charge,
        })
      }
    }
  })

  return {
    isLoading,
    isSuccess,
    isTaxBracketsSupported: true,
    brackets,
    userDefaultTaxable,
    userDefaultTaxBracket,
    userSettingShowPricesWithTaxes,
    getTaxBracket,
    getCalculatedAmounts,
  }
}
